import React from 'react';
import { Search, X, ChevronsUpDown, Minimize2, Check, Plus } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';

interface SchemaEditorToolbarProps {
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  hasChanges: boolean;
  isSavingChanges: boolean;
  onDiscardChanges: () => void;
  onSaveChanges: () => void;
  canEdit: boolean;
  onAddNewAttribute: () => void;
}

export const SchemaEditorToolbar: React.FC<SchemaEditorToolbarProps> = ({
  searchFilter,
  setSearchFilter,
  onExpandAll,
  onCollapseAll,
  hasChanges,
  isSavingChanges,
  onDiscardChanges,
  onSaveChanges,
  canEdit,
  onAddNewAttribute,
}) => {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap font-sans text-xs">
      {/* Left Controls: Search filter & Expand / Collapse */}
      <div className="flex items-center gap-2 flex-1 min-w-[280px]">
        <div className="max-w-sm flex-1">
          <Input
            size="sm"
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Filter fields by label, key, or type..."
            leftIcon={<Search size={13} />}
            rightAction={
              searchFilter ? (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setSearchFilter('')}
                  title="Clear filter"
                >
                  <X size={12} />
                </Button>
              ) : undefined
            }
          />
        </div>

        <Button
          variant="secondary"
          size="xs"
          onClick={onExpandAll}
          leftIcon={<ChevronsUpDown size={12} />}
          title="Expand All Nested Fields"
        >
          Expand All
        </Button>
        <Button
          variant="secondary"
          size="xs"
          onClick={onCollapseAll}
          leftIcon={<Minimize2 size={12} />}
          title="Collapse All Nested Fields"
        >
          Collapse All
        </Button>
      </div>

      {/* Right Controls: Save / Discard buttons + Add New Attribute Button on the RIGHT */}
      <div className="flex items-center gap-2 ml-auto">
        {hasChanges ? (
          <>
            <Button
              variant="secondary"
              size="xs"
              disabled={isSavingChanges}
              onClick={onDiscardChanges}
              leftIcon={<X size={12} />}
              title="Discard all pending modifications"
            >
              Discard
            </Button>
            <Button
              variant="primary"
              size="xs"
              disabled={isSavingChanges}
              isLoading={isSavingChanges}
              onClick={onSaveChanges}
              leftIcon={!isSavingChanges ? <Check size={12} /> : undefined}
            >
              {isSavingChanges ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-1 text-xs text-cf-textMuted mr-1 select-none">
            <Check size={12} className="text-emerald-500" />
            <span>All changes saved</span>
          </div>
        )}

        {canEdit && (
          <Button
            variant="secondary"
            size="xs"
            onClick={onAddNewAttribute}
            leftIcon={<Plus size={13} className="text-cf-orange" />}
          >
            Add New Attribute
          </Button>
        )}
      </div>
    </div>
  );
};
